// Helper to process the `stops` prop into something more useful.
//
import _ from 'lodash';

import { NullableStop, PinInfo, Route, Stop } from './stopTypes';

export const removeNullishStops = (nullableStops: NullableStop[]): Stop[] =>
    nullableStops.filter((stop) => stop.lat && stop.lng) as Stop[];

export const parseStops = (stops: Stop[]): [PinInfo[], Route[]] => {
    //////////////////////////////////////////////////////////////////////////
    // Part 1.
    //////////////////////////////////////////////////////////////////////////

    // Let's isolate the stops without a group.
    const [stopsWithoutGroup, stopsWithGroup] = _.partition(
        stops,
        (stop) => stop.group_id === null,
    );

    // Let's group the remaining stops by their group.
    const stopsByGroup = _.values(_.groupBy(stopsWithGroup, 'group_id'));

    // Within each group, let's isolate the stops without a time.
    const untimedStopsByGroup = _.map(stopsByGroup, (stopsInGroup) =>
        _.filter(stopsInGroup, (stop) => !stop.datetime),
    );
    const timedStopsByGroupUnsorted = _.map(stopsByGroup, (stopsInGroup) =>
        _.filter(stopsInGroup, (stop) => !!stop.datetime),
    );

    // Let's sort the remaining timed stops in each group by their time.
    const timedStopsByGroupsSorted = _.map(timedStopsByGroupUnsorted, (stops) =>
        _.sortBy(stops, 'datetime'),
    );

    // Let's partition the timed stops within each group into those that
    //  are part of a singleton (have <=1 timed stop in their group)
    //  and those that are long enough (2+ timed stops in their group)
    //  to draw routes for.
    //
    // We separate them out, because we don't put number badges on the singleton
    //  group stops, but we put number badges on the stops part of 2-stop-or-more routes.
    //
    // The group index determines the colour of the group, so we make sure not to
    //   change the group index. We leave a blank `[]` as spacing if needed.
    //
    const singletonGroupsOfTimedStops = _.map(timedStopsByGroupsSorted, (route) =>
        route.length <= 1 ? route : [],
    );
    const routesToDraw = _.map(timedStopsByGroupsSorted, (route) =>
        route.length >= 2 ? route : [],
    );

    //////////////////////////////////////////////////////////////////////////
    // Part 2.
    //////////////////////////////////////////////////////////////////////////

    const pinInfosForUnroutedStops: PinInfo[] = [];

    // These stops have no group, no number.
    stopsWithoutGroup.forEach((stop) =>
        pinInfosForUnroutedStops.push({
            stop,
            groupIndex: null,
            stopIndex: null,
        }),
    );

    // These stops have a group, but no number.
    untimedStopsByGroup.forEach((group, groupIndex) =>
        group.forEach((stop) =>
            pinInfosForUnroutedStops.push({
                stop,
                groupIndex,
                stopIndex: null,
            }),
        ),
    );

    // These stops are have a group, and technically they have a number,
    //  but because their group just consists of one member,
    //  we don't give them a number.
    singletonGroupsOfTimedStops.forEach((group, groupIndex) =>
        group.forEach((stop) =>
            pinInfosForUnroutedStops.push({
                stop,
                groupIndex,
                stopIndex: null,
            }),
        ),
    );

    return [pinInfosForUnroutedStops, routesToDraw];
};
